import { useState } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { ChevronDown, Loader2, Check } from 'lucide-react'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { getQuoteStatusLabel } from '@/lib/enum-utils'
import { useUpdateQuote } from '@/stores/quotes.store'
import { useSystemSettings } from '@/stores/settings.store'
import { UpdateQuoteRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface QuoteStatusDropdownProps {
  quoteId: string
  currentStatus: QuoteStatusEnum
  className?: string
}

/**
 * Interactive badge component that displays the current quote status
 * and allows changing it via a dropdown popover
 */
export function QuoteStatusDropdown({ quoteId, currentStatus, className }: QuoteStatusDropdownProps) {
  const [open, setOpen] = useState(false)
  const { updateQuote, isLoading } = useUpdateQuote()
  const { settings } = useSystemSettings()

  // Get all available statuses from system settings (excluding UNKNOWN)
  const availableStatuses = (settings?.quoteStatuses || [])
    .filter(status => status !== QuoteStatusEnum.QUOTE_STATUS_UNKNOWN)

  const handleStatusChange = async (newStatus: QuoteStatusEnum) => {
    if (newStatus === currentStatus) {
      setOpen(false)
      return
    }

    try {
      await updateQuote(
        create(UpdateQuoteRequestSchema, {
          id: quoteId,
          status: newStatus,
          updateMask: create(FieldMaskSchema, {
            paths: ['status'],
          }),
        })
      )
      toast.success(`Status updated to ${getQuoteStatusLabel(newStatus)}`)
      setOpen(false)
    } catch (error) {
      console.error('Failed to update quote status:', error)
      reportError(error, { feature: 'quote_status_update', kind: 'mutation' })
      toast.error('Failed to update status. Please try again.', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-4 py-2 text-base font-medium transition-colors',
            'border',
            isLoading && 'opacity-50 cursor-wait',
            // Apply badge color styling based on current status - matching badge.tsx variants
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_DRAFT &&
              'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_SENT &&
              'border-status-info-border bg-status-info-bg text-status-info-fg hover:opacity-90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_VIEWED &&
              'border-status-warning-border bg-status-warning-bg text-status-warning-fg hover:opacity-90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_REVISED &&
              'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED &&
              'border-status-success-border bg-status-success-bg text-status-success-fg hover:opacity-90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_REJECTED &&
              'border-status-danger-border bg-status-danger-bg text-status-danger-fg hover:opacity-90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_EXPIRED &&
              'border-destructive bg-destructive text-white hover:bg-destructive/90',
            currentStatus === QuoteStatusEnum.QUOTE_STATUS_CANCELLED &&
              'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90',
            className
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Updating...</span>
            </>
          ) : (
            <>
              <span>{getQuoteStatusLabel(currentStatus)}</span>
              <ChevronDown className="h-4 w-4" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Client visibility</p>
          {availableStatuses.map((status) => {
            const isCurrent = status === currentStatus
            const isAccepted = status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED
            return (
              <Button
                key={status}
                variant="ghost"
                size="sm"
                className={cn(
                  'w-full justify-start gap-2 h-auto py-2',
                  isCurrent && 'bg-accent'
                )}
                onClick={() => handleStatusChange(status)}
                disabled={isCurrent || isAccepted}
                title={isAccepted ? 'Accepted status is set by the customer through the portal' : undefined}
              >
                <QuoteStatusBadge status={status} className="flex-shrink-0" />
                {isCurrent && <Check className="ml-auto h-4 w-4 text-muted-foreground" />}
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
